import React, { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import "./invitedSetPassword.css";
import SignupToolbar from "../../components/lawyerToolbar/signupToolbar";
import Footer from "../../components/footer";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import PasswordField from "../../components/input/passwordField";
import { useDispatch } from "react-redux";
import { updateStatus } from "../../redux/attorneyDataSlice";
import { authenticatedFetch } from "../../network/api";

const InvitedSetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { formData, communication, userMetadata } = location.state || {};

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [criteria, setCriteria] = useState({
    length: false,
    letter: false,
    number: false,
    special: false,
  });
  const [error, setError] = useState("");
  const [alert, setAlert] = useState(null);

  // run complexity checks on every change
  useEffect(() => {
    setCriteria({
      length: newPassword.length >= 8,
      letter: /[A-Za-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
    });
  }, [newPassword]);

  const isPasswordValid = Object.values(criteria).every(Boolean);
  const passwordsMatch = newPassword && newPassword === confirmPassword;
  const canSubmit = isPasswordValid && passwordsMatch;

  console.log("User metadata is ", userMetadata);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canSubmit) {
      setError(
        !isPasswordValid
          ? "Password doesn't meet all complexity requirements."
          : "Passwords do not match."
      );
      return;
    }

    // Update Supabase auth with new password
    const { data, error: authError } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (authError) {
      setError(authError.message);
      return;
    }

    // Call own API to create/update lawyer profile
    try {
      const response = await authenticatedFetch(
        "/api/attorney/webapp/createInvitedAccount",
        {
          method: "POST",
          body: JSON.stringify({
            userId: data.user.id,
            lawyerInfo: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: userMetadata?.email,
              phone: formData.phoneNumber,
              zipCode: formData.zipCode,
              communicationPreference: communication,
              firmId: userMetadata?.firm_id,
              firmName: userMetadata?.email,
            },
          }),
        }
      );
      const result = await response.json();
      if (!result.success) {
        setError(result.message || "Failed to create profile");
        return;
      }
    } catch (apiError) {
      setError("Error creating profile. Please try again.");
      return;
    }

    // On success
    dispatch(updateStatus("PASSWORD_SET_SUCCESS"));
    setAlert({
      severity: "success",
      message: "Password updated & profile created!",
    });
    setTimeout(() => {
      navigate("/grow/invited/redirect");
    }, 1500);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <SignupToolbar />

      {alert && (
        <Alert severity={alert.severity} style={{ margin: "20px 30px 0" }}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ margin: "20px 30px 0" }}>
          {error}
        </Alert>
      )}

      <div className="update-password-container">
        <form
          className="update-password-form-wrapper"
          onSubmit={handleSubmit}
          noValidate
        >
          <h2 className="update-password-heading">Create a new password</h2>
          <h3 className="update-password-subtitle">
            Please choose a strong new password
          </h3>

          <PasswordField
            title="New Password"
            placeholder="Enter your new password"
            isRequired
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          <ul className="password-criteria">
            <li className={criteria.length ? "valid" : "invalid"}>
              At least 8 characters
            </li>
            <li className={criteria.letter ? "valid" : "invalid"}>
              A letter (A–Z or a–z)
            </li>
            <li className={criteria.number ? "valid" : "invalid"}>
              A number (0–9)
            </li>
            <li className={criteria.special ? "valid" : "invalid"}>
              A special character (e.g. @#$%*)
            </li>
          </ul>

          <PasswordField
            title="Confirm Password"
            placeholder="Re‑enter your new password"
            isRequired
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <button
            type="submit"
            className="update-password-button"
            disabled={!canSubmit}
          >
            Continue
          </button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default InvitedSetPassword;
